/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnSortEvent } from 'primereact/column';
import { ConditionType } from 'pages/thermography/model/enumerations/condition-type.model';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { UilCheck, UilInfoCircle, UilExclamationTriangle, UilExclamationOctagon } from "@iconscout/react-unicons";
import { IEquipmentComponentTemperatureLimits } from './model/equipment-component-temperature-limits.model';
import { EquipmentType } from './model/enumerations/equipment-type.model';
import i18n from 'i18n/i18n';
import { getDateLocaleConfig } from 'i18n/locale';

dayjs.extend(customParseFormat);

export const THERMAL_ANOMALY = "CAT";
export const THERMAL_RECORD = "TRR";

export const parseDate = (date: string | dayjs.Dayjs | null): Date => {
    if (!date) throw new Error("Invalid date: null value provided");
    if (typeof date === 'string') {
        const raw = date.trim();
        if (!raw) throw new Error("Invalid date string: empty value provided");

        const localeFormats = getDateLocaleConfig().parseFormats;
        const fallbackFormats = ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss'];

        let parsed = dayjs(raw, [...localeFormats, ...fallbackFormats], true);
        if (!parsed.isValid()) {
            // fallback para datas ISO/strings nativas quando já vêm completas da API
            parsed = dayjs(raw);
        }
        if (!parsed.isValid()) throw new Error(`Invalid date string: ${date}`);
        return parsed.toDate();
    }
    return date.toDate();
};

export const sortDate = (e: ColumnSortEvent | any) => {
    const dataToSort = e && e.data ? e.data : e;
    const order = e && e.order !== undefined ? e.order : 1;
    const field = e.field;
    // const field = e.field || 'datetime';

    const result = [...dataToSort].sort((a, b) => {
        const rawA = a[field];
        const rawB = b[field];

        // Se a data for vazia/null/undefined, assume um valor extremo
        const dateA = rawA ? parseDate(rawA) : new Date(0);
        const dateB = rawB ? parseDate(rawB) : new Date(0);

        return order * (dateA.getTime() - dateB.getTime());
    });

    return result;
}


export const sortCondition = (e: ColumnSortEvent | any) => {
    const dataToSort = e && e.data ? e.data : e;
    const order = e && e.order !== undefined ? e.order : 1;
    const field = e.field;

    const result = [...dataToSort].sort((a, b) => {
        const conditionKeyA = a[field] ?? '';
        const conditionKeyB = b[field] ?? '';

        // Get display values from ConditionType enum
        const conditionA = conditionKeyA ? ConditionType[conditionKeyA] : '';
        const conditionB = conditionKeyB ? ConditionType[conditionKeyB] : '';

        const priorityA = conditionPriority[conditionA] ?? 0;
        const priorityB = conditionPriority[conditionB] ?? 0;

        return order * (priorityA - priorityB);
    });

    return result;
};

export const formatDate = (date: Date): string => {
    const formatted = new Intl.DateTimeFormat(getDateLocaleConfig().intlLocale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).format(date);
    return formatted;
}

export const formatDatetime = (date: Date): string => {
    const formatted = new Intl.DateTimeFormat(getDateLocaleConfig().intlLocale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false // 24h
    }).format(date);
    return formatted;
}


export const getDeadlineLabels = () => [
    i18n.t('pages.thermography.deadlineBuckets.expired', { ns: 'translation' }),
    i18n.t('pages.thermography.deadlineBuckets.lessThan30Days', { ns: 'translation' }),
    i18n.t('pages.thermography.deadlineBuckets.moreThan30Days', { ns: 'translation' })
];

export const getMonitoringLabels = () => [
    i18n.t('pages.thermography.deadlineBuckets.expired', { ns: 'translation' }),
    i18n.t('pages.thermography.deadlineBuckets.lessThan30Days', { ns: 'translation' }),
    i18n.t('pages.thermography.deadlineBuckets.moreThan30Days', { ns: 'translation' })
];

export const getRouteDeadlineLabels = () => [
    i18n.t('pages.thermography.deadlineBuckets.routeDelayed', { ns: 'translation' }),
    i18n.t('pages.thermography.deadlineBuckets.lessThan30Days', { ns: 'translation' }),
    i18n.t('pages.thermography.deadlineBuckets.moreThan30Days', { ns: 'translation' })
];

export type ModeFormProps = {
    mode: 'new' | 'new_trr' | 'edit_trr' | 'edit' | 'view';
};

export const conditionPriority: Record<string, number> = {
    'NORMAL': 1,
    'LOW_RISK': 2,
    'MEDIUM_RISK': 3,
    'HIGH_RISK': 4,
    'IMMINENT_HIGH_RISK': 5,
};
export const periodList = [
    { id: 1, name: '1 dia' },
    { id: 7, name: '7 dias' },
    { id: 15, name: '15 dias' },
    { id: 30, name: '30 dias' },
    { id: 45, name: '45 dias' },
    { id: 90, name: '3 meses' },
    { id: 180, name: '6 meses' },
    { id: 0, name: 'A ser definido' }
];

export const conditionList = [
    { id: '1', name: 'Normal' },
    { id: '2', name: 'Anormal' },
    { id: '3', name: 'Fora de Operação' },

];

export const phaseList = [
    { id: '1', name: 'A' },
    { id: '2', name: 'B' },
    { id: '3', name: 'V' },
];


export interface RiskLimit {
    limit: string; // Ex: ">10 ≤30"
    risk: string; // Ex: "Baixo Risco"
}

export function getRisk(value: number, condition: string): boolean {
    if (condition.toLowerCase() === "não aplicável" || condition.toLowerCase() === "na" || condition.trim() === "")
        return false;

    const conditions = condition.split(" ");
    for (const cond of conditions) {
        const match = cond.match(/(≤|≥|<|>|=)(\d+(\.\d+)?)/);
        if (!match) continue;

        const operator = match[1];
        const limit = parseFloat(match[2]);

        switch (operator) {
            case "<":
                if (!(value < limit)) return false;
                break;
            case ">":
                if (!(value > limit)) return false;
                break;
            case "≤":
                if (!(value <= limit)) return false;
                break;
            case "≥":
                if (!(value >= limit)) return false;
                break;
            case "=":
                if (!(value === limit)) return false;
                break;
        }
    }
    return true; // Passou por todas as condições sem falhar
}

export function classifyRisk(
    value: number,
    equipment_component_limit: IEquipmentComponentTemperatureLimits
): ConditionType {
    if (getRisk(value, equipment_component_limit.normal))
        return ConditionType.NORMAL;

    if (getRisk(value, equipment_component_limit.lowRisk))
        return ConditionType.LOW_RISK;

    if (getRisk(value, equipment_component_limit.mediumRisk))
        return ConditionType.MEDIUM_RISK;

    if (getRisk(value, equipment_component_limit.highRisk))
        return ConditionType.HIGH_RISK;

    if (getRisk(value, equipment_component_limit.imminentHighRisk))
        return ConditionType.IMMINENT_HIGH_RISK;

    return ConditionType.NORMAL; // Default fallback, though ideally should never reach here
}

export const equipmentComponentGroupMap = new Map<EquipmentType, string>([
    [EquipmentType.AUTOTRANSFORMER, 'TF'],
    [EquipmentType.POWER_TRANSFORMER, 'TF'],
    [EquipmentType.SHUNT_REACTOR, 'TF'],
    [EquipmentType.CURRENT_TRANSFORMER, 'TC'],
    [EquipmentType.POTENTIAL_TRANSFORMER, 'TP'],
    [EquipmentType.CIRCUIT_BREAKER, 'DJ'],
    [EquipmentType.DISCONNECT_SWITCH, 'SC'],
    [EquipmentType.SURGE_ARRESTER, 'PR'],
    [EquipmentType.CAPACITOR_BANK, 'CA'],
    [EquipmentType.BATTERY_BANK, 'BT'],
    [EquipmentType.ELECTRICAL_PANEL, 'PA'],
    [EquipmentType.PROTECTION_CONTROL_PANEL, 'PA'],
    [EquipmentType.EMERGENCY_GENERATOR, 'GE'],
    [EquipmentType.METAL_CLAD_SWITCHGEAR, 'CB'],
    [EquipmentType.BUSBAR, 'BR'],
    [EquipmentType.DISTRIBUTION_LINE, 'RD'],
    [EquipmentType.TRANSMISSION_TOWER, 'TR']
]);

type StyleCfg = {
    background: string;
    color: string;
    border: string;
    Icon: React.ComponentType<{ size?: number; color?: string }>;
};

export const CONDITION_STYLE: Record<ConditionType, StyleCfg> = {
    "NORMAL": {
        background: "#d1e7dd", // pastel verde
        color: "#0f5132",
        border: "#badbcc",
        Icon: UilCheck
    },

    "LOW_RISK": {
        background: "#cff4fc",   // pastel azul
        color: "#055160",
        border: "#b6effb",
        Icon: UilInfoCircle
    },

    "MEDIUM_RISK": {
        background: "#fff3cd",   // pastel amarelo
        color: "#664d03",
        border: "#ffecb5",
        Icon: UilExclamationTriangle
    },

    "HIGH_RISK": {
        background: "#ffe5d0",   // pastel laranja
        color: "#7a2e00",
        border: "#ffd1ad",
        Icon: UilExclamationTriangle
    },

    "IMMINENT_HIGH_RISK": {
        background: "#f8d7da",   // pastel vermelho
        color: "#842029",
        border: "#f5c2c7",
        Icon: UilExclamationOctagon
    }
};

const CONDITION_BG_CLASS: Record<Exclude<ConditionType, "NORMAL">, string> = {
    "LOW_RISK": "bg-blue",
    "MEDIUM_RISK": "bg-yellow",
    "HIGH_RISK": "bg-orange",
    "IMMINENT_HIGH_RISK": "bg-red",
};

export function getConditionBgClass(condition?: string | null): string {
    if (!condition || condition === "NORMAL") return "";
    return CONDITION_BG_CLASS[condition as Exclude<ConditionType, "NORMAL">] ?? "";
}
